import { List } from "@refinedev/antd";
import { useApiUrl, useCustomMutation, useNotification } from "@refinedev/core";
import { Button, Card, Col, Row, Typography } from "antd";
import { PlayCircleOutlined, LoadingOutlined, DownloadOutlined } from "@ant-design/icons";
import React from "react";
import FileSaver from "file-saver";
import axios from "axios";
import { TOKEN_KEY } from "authProvider";

// The report endpoints are now guarded (Task 2.7). handleGenerate goes through the
// refine dataProvider's authed axios instance, but handleDownload uses raw axios,
// so it must attach the bearer token explicitly or the guard returns 401.
const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
});

interface ReportConfig {
  key: string;
  title: string;
  description: string;
  endpoint: string;
  downloadFilename: string;
}

const REPORTS: ReportConfig[] = [
  {
    key: "vessel-subscription",
    title: "Vessel Subscription Report",
    description: "Lists all vessels with their IMO number, UKHO licence, shipco, subscription hold status, and AVCS/ADP/ENP subscription types.",
    endpoint: "/scheduler/vessel-subscription-report",
    downloadFilename: "vessel-subscription-report.csv"
  },
  {
    key: "entm-vessels",
    title: "eNTM Vessels Report",
    description: "Lists all eNTM-enrolled vessels with their UKHO licence, shipco, contact emails, hold status, and email/transmission size limits.",
    endpoint: "/scheduler/entm-vessels-report",
    downloadFilename: "entm-vessels-report.csv"
  },
  {
    key: "licence-reconciliation",
    title: "Licence Reconciliation Report",
    description: "Reconciles licences between UKHO and eMariner. Detects deactivated licences, new licences not yet in eMariner, and product type drift (mismatched service types: AVCS, ARCS, ENP, ADP).",
    endpoint: "/scheduler/licence-reconciliation-report",
    downloadFilename: "licence-reconciliation-report.csv"
  },
];

export const OnDemandReportsList: React.FC = () => {
  const apiUrl = useApiUrl();
  const { open } = useNotification();
  const [loadingKey, setLoadingKey] = React.useState<string | null>(null);
  const { mutate } = useCustomMutation();

  const handleGenerate = (report: ReportConfig) => {
    setLoadingKey(report.key);
    mutate(
      {
        url: `${apiUrl}${report.endpoint}`,
        method: "post",
        values: {},
      },
      {
        onError: (error) => {
          open?.({
            type: "error",
            message: "Report Generation Failed",
            description: error.message || "An error occurred while generating the report.",
          });
          setLoadingKey(null);
        },
        onSuccess: () => {
          open?.({
            type: "success",
            message: "Report Generated",
            description: `${report.title} has been generated and sent to configured recipients.`,
          });
          setLoadingKey(null);
        },
      }
    );
  };

  const handleDownload = async (report: ReportConfig) => {
    setLoadingKey(`download-${report.key}`);
    try {
      const response = await axios.post(
        `${apiUrl}${report.endpoint}?download=true`,
        {},
        { responseType: "blob", headers: getAuthHeader() }
      );
      FileSaver.saveAs(response.data, report.downloadFilename);
      open?.({
        type: "success",
        message: "Report Downloaded",
        description: `${report.title} has been downloaded.`,
      });
    } catch (error: any) {
      open?.({
        type: "error",
        message: "Download Failed",
        description: error.message || "An error occurred while downloading the report.",
      });
    }
    setLoadingKey(null);
  };

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "On-Demand Reports",
        subTitle: "Generate reports manually",
      }}
    >
      <Row gutter={[16, 16]}>
        {REPORTS.map((report) => (
          <Col xs={24} sm={24} md={12} lg={12} xl={8} key={report.key}>
            <Card
              style={{ height: "100%", display: "flex", flexDirection: "column" }}
              styles={{ body: { flex: 1 } }}
              title={report.title}
              actions={[
                <Button
                  key="generate"
                  type="primary"
                  icon={loadingKey === report.key ? <LoadingOutlined /> : <PlayCircleOutlined />}
                  disabled={loadingKey !== null}
                  loading={loadingKey === report.key}
                  onClick={() => handleGenerate(report)}
                >
                  Generate & Email
                </Button>,
                <Button
                  key="download"
                  icon={loadingKey === `download-${report.key}` ? <LoadingOutlined /> : <DownloadOutlined />}
                  disabled={loadingKey !== null}
                  loading={loadingKey === `download-${report.key}`}
                  onClick={() => handleDownload(report)}
                >
                  Download
                </Button>,
              ]}
            >
              <Typography.Paragraph type="secondary">
                {report.description}
              </Typography.Paragraph>
            </Card>
          </Col>
        ))}
      </Row>
    </List>
  );
};
